// src/components/AnswerActions.tsx
import React, { useState, useRef, useEffect } from "react";
import {
  makeStyles,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Field,
  Input,
  Textarea,
  Toaster,
  useToastController,
  tokens,
  Toast,
  ToastTitle,
  useId,
  Spinner,
  mergeClasses,
} from "@fluentui/react-components";
import {
  ThumbLike16Filled,
  ThumbLike16Regular,
  ThumbDislike16Filled,
  ThumbDislike16Regular,
  Copy16Regular,
  Lightbulb20Regular,
  MoreHorizontal20Regular,
  Document16Regular,
  MarkdownRegular,
  Warning16Regular,
  Speaker120Regular,
  Speaker120Filled,
} from "@fluentui/react-icons";
import { sendFeedback, sendReport, synthesizeSpeech, RateLimitError } from "../api/api";
import { ResponseLanguage, UserFeedbackMessage, UserReportMessage } from "../api/models";
import {
  AudioService,
  STT_RECORDING_STARTED_EVENT,
  TTS_ACTIVITY_STARTED_EVENT,
  beginAudioPlayback,
  beginAudioSynthesis,
  completeAudioSynthesis,
  getAudioPlaybackInterruptionVersion,
  stopAllAudioPlayback,
} from "../services/audioService";
import { getBrandConfig } from '../brands/brand-registry';
import { getTtsVoiceForResponse, TtsFeatureConfig } from '../services/ttsVoice';

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    gap: "0px", // No gap for tighter spacing between buttons
    marginTop: "var(--pill-citation-margin-top, 4px)",
    marginBottom: "var(--pill-citation-margin-bottom, 4px)",
    justifyContent: "flex-end",
    flexWrap: "wrap", // Allow wrapping on small screens
    flex: "1 1 auto", // Take remaining space, align to right
    minWidth: 0, // Allow flex item to shrink
    '@media (max-width: 600px)': {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: 'auto auto',
      gap: 'var(--answer-actions-gap-mobile, 2px)',
      marginTop: 'var(--answer-actions-margin-top-mobile, 2px)',
      marginBottom: 'var(--answer-actions-margin-bottom-mobile, 2px)',
      justifyContent: 'stretch',
      width: '100%',
      maxWidth: 'fit-content',
    },
  },
  button: {
    minWidth: "44px", // WCAG 2.2 Level AA: Minimum 44x44px touch target
    minHeight: "44px",
    padding: tokens.spacingHorizontalXS, // Reduced padding for tighter spacing
    color: tokens.colorNeutralForeground3, // Light grey for ghost icons
    backgroundColor: "transparent",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2,
      color: tokens.colorNeutralForeground1,
    },
    '@media (max-width: 600px)': {
      minWidth: 'var(--answer-actions-button-width-mobile, 36px)',
      minHeight: 'var(--answer-actions-button-height-mobile, 36px)',
      padding: tokens.spacingHorizontalXS,
      width: '100%',
      aspectRatio: '1', // Make buttons square on mobile
    },
  },
  buttonReady: {
    color: tokens.colorPaletteGreenForeground2,
    fontWeight: tokens.fontWeightSemibold,
    '&:hover': {
      color: tokens.colorPaletteGreenForeground2,
    },
    '@media (max-width: 600px)': {
      fontSize: 'var(--answer-actions-button-ready-font-size-mobile, 12px)',
    },
  },
  voiceButtonMobile: {
    '@media (max-width: 600px)': {
      display: 'none', // Hide voice synthesis button on mobile to maintain 2x2 grid
    },
  },
  dialogBody: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    '@media (max-width: 600px)': {
      gap: tokens.spacingVerticalXS,
    },
  },
  dialogActions: {
    justifyContent: "flex-end",
    marginTop: tokens.spacingVerticalM,
    '@media (max-width: 600px)': {
      gap: tokens.spacingVerticalXS,
    },
  },
  eventList: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingVerticalS,
    paddingRight: tokens.spacingVerticalS,
    width: "100%",
    wordWrap: "break-word",
    overflow: "auto",
    maxHeight: "400px",
    "& ol": {
      marginTop: tokens.spacingVerticalS,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: tokens.spacingVerticalM,
      paddingRight: 0,
    },
    "& li": {
      marginTop: tokens.spacingVerticalXS,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
    },
  },
  eventDialogSurface: {
    minWidth: "360px",
    maxWidth: "480px",
    '@media (max-width: 600px)': {
      minWidth: 'var(--answer-actions-dialog-surface-width-mobile, 320px)',
      maxWidth: 'var(--answer-actions-dialog-surface-width-mobile, 420px)',
    },
  },
});

interface Props {
  id: string;
  messageId: string;
  question: string; // User's question for export context
  content: string;
  citations: string[]; // Used in parent component for CitationPills
  eventMessages: string[];
  prefetchedAudio?: string;
  prefetchedVoice?: string;
  prefetchedSpeed?: number;
  voiceSynthesisStatus?: "idle" | "pending" | "ready" | "error";
  responseLanguage?: ResponseLanguage;
  onRateLimitError?: (error: any) => void;
}

export const AnswerActions: React.FC<Props> = ({
  id,
  messageId,
  question,
  content,
  eventMessages,
  prefetchedAudio,
  prefetchedVoice,
  prefetchedSpeed,
  voiceSynthesisStatus = "idle",
  responseLanguage,
  onRateLimitError,
}) => {
  const styles = useStyles();
  const brandConfig = getBrandConfig();
  const speechProvider = (brandConfig.features as { speechProvider?: 'openai' | 'abair' }).speechProvider || 'openai';
  const showVoiceSettings = 'showVoiceSettings' in brandConfig.features
    ? brandConfig.features.showVoiceSettings
    : true;
  const configuredResponseVoice = getTtsVoiceForResponse(
    brandConfig.features as TtsFeatureConfig,
    responseLanguage,
  );
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [eventsDialogOpen, setEventsDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [cachedAudio, setCachedAudio] = useState<string | null>(prefetchedAudio ?? null);
  const [cachedSpeed, setCachedSpeed] = useState<number | null>(prefetchedSpeed ?? null);
  const audioServiceRef = useRef(new AudioService());

  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);

  useEffect(() => {
    setCachedAudio(prefetchedAudio ?? null);
    setCachedSpeed(prefetchedSpeed ?? null);
  }, [prefetchedAudio, prefetchedSpeed, messageId]);

  useEffect(() => {
    return () => {
      audioServiceRef.current.destroy();
    };
  }, []);

  useEffect(() => {
    const onTtsActivityStarted = (event: Event) => {
      const activeMessageId = (event as CustomEvent<string>).detail;
      if (activeMessageId !== messageId) {
        audioServiceRef.current.stop();
        setIsPlaying(false);
        setIsSynthesizing(false);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(TTS_ACTIVITY_STARTED_EVENT, onTtsActivityStarted);
      return () => window.removeEventListener(TTS_ACTIVITY_STARTED_EVENT, onTtsActivityStarted);
    }

    return undefined;
  }, [messageId]);

  useEffect(() => {
    const onSttRecordingStarted = () => {
      audioServiceRef.current.stop();
      setIsPlaying(false);
      setIsSynthesizing(false);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(STT_RECORDING_STARTED_EVENT, onSttRecordingStarted);
      return () => {
        window.removeEventListener(STT_RECORDING_STARTED_EVENT, onSttRecordingStarted);
      };
    }

    return undefined;
  }, []);

  useEffect(() => {
    setIsPlaying(false);
    setIsSynthesizing(false);
    audioServiceRef.current.stop();
  }, [messageId]);

  const getStoredSpeed = () => {
    if (!showVoiceSettings || typeof window === "undefined") {
      return 1;
    }
    const speedSetting = localStorage.getItem("speechSpeed");
    return speedSetting === "faster" ? 0.5 : 1;
  };

  const isPrefetchPending = voiceSynthesisStatus === "pending";
  const storedSpeed = getStoredSpeed();
  const hasCachedAudio = !!cachedAudio && (cachedSpeed === null || cachedSpeed === storedSpeed);
  const isReadyHighlight = hasCachedAudio && !isSynthesizing && !isPlaying;
  const showLoadingIcon = isSynthesizing || (isPrefetchPending && !hasCachedAudio);
  const isSpeakUnavailable = !isPlaying && showLoadingIcon;
  const speakButtonIcon = showLoadingIcon
    ? <Spinner size="tiny" aria-hidden="true" />
    : (isPlaying ? <Speaker120Filled /> : <Speaker120Regular />);
  const speakButtonLabel = `${isPlaying ? brandConfig.actions.buttons.stopAudio : brandConfig.actions.buttons.playAudio}: ${question || brandConfig.actions.buttons.thisAnswer}`;

  const showToast = (message: string, type: "success" | "error" | "warning") => {
    dispatchToast(
      <Toast>
        <ToastTitle>{message}</ToastTitle>
      </Toast>,
      { intent: type }
    );
  };

  const handleLike = async () => {
    setLiked(!liked);
    setDisliked(false);
    
    // Validate that we have a valid conversation ID
    if (!id || id.trim() === '') {
      console.warn('Cannot send feedback: conversation ID is empty');
      showToast('Please wait for the response to complete before providing feedback', 'warning');
      return;
    }
    
    try {
      const feedbackMessage: UserFeedbackMessage = {
        messageId: messageId,
        id: id,
        feedback: "like"
      };
      await sendFeedback(feedbackMessage);
      showToast(brandConfig.actions.toasts.feedbackSubmittedLike, "success");
    } catch (error) {
      if (error instanceof RateLimitError && onRateLimitError) {
        onRateLimitError(error);
        return;
      }
      console.error("Error sending like feedback:", error);
      showToast(brandConfig.actions.toasts.errorSendingLike + error, "error");
    }
  };

  const handleDislike = async () => {
    setDisliked(!disliked);
    setLiked(false);
    
    // Validate that we have a valid conversation ID
    if (!id || id.trim() === '') {
      console.warn('Cannot send feedback: conversation ID is empty');
      showToast('Please wait for the response to complete before providing feedback', 'warning');
      return;
    }
    
    try {
      const feedbackMessage: UserFeedbackMessage = {
        messageId: messageId,
        id: id,
        feedback: "dislike",
      };
      await sendFeedback(feedbackMessage);
      showToast(brandConfig.actions.toasts.feedbackSubmittedDislike, "warning");
    } catch (error) {
      if (error instanceof RateLimitError && onRateLimitError) {
        onRateLimitError(error);
        return;
      }
      console.error("Error sending dislike feedback:", error);
      showToast(brandConfig.actions.toasts.errorSendingDislike + error, "error");
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content).then(() => {
      showToast(brandConfig.actions.toasts.answerCopied, "success");
    });
  };

  const handleExportMarkdown = () => {
    navigator.clipboard.writeText(content);
    showToast(brandConfig.actions.toasts.answerCopiedMarkdown, "success");
  };

  const handleExportText = () => {
    // Create accessible plain text export with question and answer
    const brandName = brandConfig.appName || 'Chatbot';
    const timestamp = new Date().toLocaleString();
    
    const textContent = [
      `${brandName} - Conversation Export`,
      `Exported: ${timestamp}`,
      '',
      '--- Question ---',
      question || '(No question recorded)',
      '',
      '--- Answer ---',
      content,
      '',
      '---',
      `Generated by ${brandName}`,
    ].join('\n');
    
    // Create and download .txt file
    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conversation-${messageId}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showToast(brandConfig.actions.toasts.answerExportedText || 'Conversation exported as text file', "success");
  };

  const handleReport = () => {
    setReportDialogOpen(true);
  };

  const handleViewEvents = () => {
    setEventsDialogOpen(true);
  };

  const handleSubmitReport = async () => {
    try {
      const reportMessage: UserReportMessage = {
        messageId: messageId,
        id: id,
        subject: reportReason,
        description: reportDetails
      };
      await sendReport(reportMessage);
      showToast(brandConfig.actions.toasts.reportSubmitted, "success");
      setReportDialogOpen(false);
      setReportReason("");
      setReportDetails("");
    } catch (error) {
      if (error instanceof RateLimitError && onRateLimitError) {
        onRateLimitError(error);
        return;
      }
      console.error("Error submitting report:", error);
      showToast(brandConfig.actions.toasts.errorSubmittingReport + error, "error");
    }
  };

  const handleSpeak = async () => {
    if (!configuredResponseVoice) {
      return;
    }

    if (isPlaying) {
      stopAllAudioPlayback();
      setIsPlaying(false);
      return;
    }

    if (isSpeakUnavailable) {
      return;
    }

    const currentStoredSpeed = getStoredSpeed();

    if (cachedAudio && (cachedSpeed === null || cachedSpeed === currentStoredSpeed)) {
      const interruptionVersionAtStart = beginAudioPlayback(messageId);

      try {
        await audioServiceRef.current.playBase64Audio(
          cachedAudio,
          () => setIsPlaying(false),
          () => setIsPlaying(false)
        );

        if (interruptionVersionAtStart !== getAudioPlaybackInterruptionVersion()) {
          setIsPlaying(false);
          return;
        }

        setIsPlaying(true);
        showToast(brandConfig.actions.toasts.playingAudio, "success");
      } catch (error) {
        console.error("Error playing cached audio:", error);
        showToast(brandConfig.actions.toasts.failedToSynthesize + error, "error");
      }
      return;
    }

    const synthesisRequest = beginAudioSynthesis(messageId);
    const { controller, interruptionVersion: interruptionVersionAtStart } = synthesisRequest;

    try {
      setIsSynthesizing(true);
      // Get selected voice and speed from localStorage, fallback to defaults
      const selectedVoice = showVoiceSettings
        ? prefetchedVoice || localStorage.getItem('selectedVoice') || configuredResponseVoice
        : configuredResponseVoice;
      const selectedSpeed = currentStoredSpeed;

      const response = await synthesizeSpeech(
        content,
        messageId,
        selectedVoice,
        selectedSpeed,
        speechProvider,
        controller.signal,
      );
      if (!response?.audioContent) {
        throw new Error("Synthesis response missing audio content");
      }

      if (controller.signal.aborted || interruptionVersionAtStart !== getAudioPlaybackInterruptionVersion()) {
        return;
      }

      await audioServiceRef.current.playBase64Audio(
        response.audioContent,
        () => setIsPlaying(false),
        () => setIsPlaying(false)
      );

      if (interruptionVersionAtStart !== getAudioPlaybackInterruptionVersion()) {
        setIsPlaying(false);
        return;
      }

      setCachedAudio(response.audioContent);
      setCachedSpeed(response.speedUsed ?? selectedSpeed);
      setIsPlaying(true);
      showToast(brandConfig.actions.toasts.playingAudio, "success");
    } catch (error) {
      if (controller.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
        return;
      }
      console.error("Error synthesizing speech:", error);
      showToast(brandConfig.actions.toasts.failedToSynthesize + error, "error");
    } finally {
      completeAudioSynthesis(controller);
      setIsSynthesizing(false);
    }
  };

  // getTitleFromUrl function removed - citations now handled by CitationPills component

  return (
    <div className={styles.container}>
      {/* 1. Like */}
      <Button
        className={styles.button}
        icon={liked ? <ThumbLike16Filled /> : <ThumbLike16Regular />}
        appearance="subtle"
        onClick={handleLike}
        title={brandConfig.actions.buttons.like}
        aria-label={brandConfig.actions.buttons.like}
      />

      {/* 2. Dislike */}
      <Button
        className={styles.button}
        icon={disliked ? <ThumbDislike16Filled /> : <ThumbDislike16Regular />}
        appearance="subtle"
        onClick={handleDislike}
        title={brandConfig.actions.buttons.dislike}
        aria-label={brandConfig.actions.buttons.dislike}
      />

      {/* 3. Copy */}
      <Button
        className={styles.button}
        icon={<Copy16Regular />}
        appearance="subtle"
        onClick={handleCopy}
        title={brandConfig.actions.buttons.copy}
        aria-label={brandConfig.actions.buttons.copy}
      />

      {/* 4. Voice Synthesis (conditional on brand feature flag) */}
      {configuredResponseVoice && (
        <Button
          className={mergeClasses(styles.button, isReadyHighlight && styles.buttonReady, styles.voiceButtonMobile)}
          icon={speakButtonIcon}
          appearance="subtle"
          onClick={handleSpeak}
          aria-disabled={isSpeakUnavailable}
          aria-busy={showLoadingIcon}
          title={isPlaying ? brandConfig.actions.buttons.stopAudio : brandConfig.actions.buttons.playAudio}
          aria-label={speakButtonLabel}
          aria-pressed={isPlaying}
        />
      )}

      {/* 4. 3-dot menu (Bulb/Events + Export + Report) */}
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button
            className={styles.button}
            icon={<MoreHorizontal20Regular />}
            appearance="subtle"
            title={brandConfig.actions.buttons.moreActions}
            aria-label={brandConfig.actions.buttons.moreActions}
          />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {eventMessages.length > 0 && (
              <MenuItem onClick={handleViewEvents} icon={<Lightbulb20Regular />}>
                {brandConfig.actions.buttons.viewThoughtProcess}
              </MenuItem>
            )}
            {(brandConfig.features as { enableMarkdownExport?: boolean }).enableMarkdownExport !== false && (
              <MenuItem onClick={handleExportMarkdown} icon={<MarkdownRegular />}>
                {brandConfig.actions.menu.exportMarkdown}
              </MenuItem>
            )}
            <MenuItem onClick={handleExportText} icon={<Document16Regular />}>
              {brandConfig.actions.menu.exportText || 'Export - Text'}
            </MenuItem>
            <MenuItem onClick={handleReport} icon={<Warning16Regular />}>
              {brandConfig.actions.menu.report}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>

      {/* Events Dialog */}
      {eventMessages.length > 0 && (
        <Dialog open={eventsDialogOpen} onOpenChange={(_e, data) => setEventsDialogOpen(data.open)}>
          <DialogSurface 
            className={styles.eventDialogSurface}
            aria-describedby="events-dialog-description"
          >
            <DialogBody className={styles.dialogBody}>
              <DialogTitle>{brandConfig.actions.labels.eventHistory}</DialogTitle>
              <div 
                id="events-dialog-description" 
                className={styles.eventList}
              >
                <ol>
                  {eventMessages
                    .filter((s) => s != "")
                    .map((evt, idx) => (
                      <li key={idx}>{evt}</li>
                    ))}
                </ol>
              </div>
            </DialogBody>
            <DialogActions className={styles.dialogActions}>
              <Button appearance="primary" onClick={() => setEventsDialogOpen(false)}>
                {brandConfig.actions.buttons.closeDialog || "Close"}
              </Button>
            </DialogActions>
          </DialogSurface>
        </Dialog>
      )}

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={(_e, data) => setReportDialogOpen(data.open)}>
        <DialogSurface aria-describedby="report-dialog-description">
          <DialogBody className={styles.dialogBody}>
            <DialogTitle>{brandConfig.actions.reportDialog.title}</DialogTitle>
            <p id="report-dialog-description" className="sr-only">
              {brandConfig.actions.reportDialog.description || 'Use this form to provide feedback or report an issue with this answer.'}
            </p>
            <Field 
              label={brandConfig.actions.reportDialog.reasonLabel}
              required
            >
              <Input 
                value={reportReason} 
                onChange={(e) => setReportReason(e.target.value)}
                aria-required="true"
              />
            </Field>
            <Field label={brandConfig.actions.reportDialog.detailsLabel}>
              <Textarea 
                value={reportDetails} 
                onChange={(e) => setReportDetails(e.target.value)}
              />
            </Field>
          </DialogBody>
          <DialogActions className={styles.dialogActions}>
            <Button appearance="secondary" onClick={() => setReportDialogOpen(false)}>
              {brandConfig.actions.reportDialog.cancelButton}
            </Button>
            <Button 
              appearance="primary" 
              onClick={handleSubmitReport}
              disabled={!reportReason.trim()}
            >
              {brandConfig.actions.reportDialog.submitButton}
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      <Toaster toasterId={toasterId} />
    </div>
  );
};
